import { API_URL } from '../../lib/config.ts';
import { HotelBookingType } from '../../globalModels/common/hotelBookingType.ts';
import { queryStringArray, roots } from './utils.ts';
import { ComposeEndpoints } from './composeEndpoints.ts';
import { FolioEndpoints } from './folioEndpoints.ts';
import { ProjectEndpoints } from './projectEndpoints.ts';
import { ReportingEndpoints } from './reportingEndpoints.ts';
import { InboxHealthTargetType } from 'src/features/InboxHealth/types.ts';
import { CompanyContactType } from 'src/enums/crm/CompanyContactType';

export const ApiEndpoints = {
  dataAccess: {
    getFile: (handle: string) => `/api/File?fileHandle=${handle}`
  },
  emailProcessing: {
    health: (target: InboxHealthTargetType) => `/api/EmailProcessing/health/${target}`
  },
  folioProcessing: {
    folioHealth: '/api/FolioProcessing/health'
  },
  support: {
    base: '/api/support/requests',
    settings: '/api/support/requests/settings',
    getById: (ticketId: string) => `/api/support/requests/${ticketId}`,
    newCount: '/api/support/requests/count/new',
    linear: '/api/support/requests/linear',
    hotelRateScreenshot: '/api/support/hotel-rate-screenshot',
    toDev: (ticketId: string) => `/api/support/requests/${ticketId}/to-dev`,
    fixNext: (ticketId: string) => `/api/support/requests/${ticketId}/fix-next`,
    urgentFix: (ticketId: string) => `/api/support/requests/${ticketId}/urgent-fix`,
    linearFixNext: (externalId: string) => `/api/support/requests/linear/${encodeURIComponent(externalId)}/fix-next`,
    linearUrgentFix: (externalId: string) =>
      `/api/support/requests/linear/${encodeURIComponent(externalId)}/urgent-fix`,
    linearAddComment: (externalId: string) => `/api/support/requests/linear/${encodeURIComponent(externalId)}/comments`
  },
  userProfile: {
    tutorials: '/api/UserProfile/tutorials',
    tutorialsBulk: '/api/UserProfile/tutorials/bulk',
    addTutorial: (userId: string, tutorialName: string) => `/api/UserProfile/${userId}/tutorials/${tutorialName}`,
    removeAllTutorials: (userId: string) => `/api/UserProfile/${userId}/tutorials`
  },
  selectOptions: {
    tags: {
      city: 'api/selectoption/tags/city',
      contact: 'api/selectoption/tags/contact'
    },
    approvals: {
      availableRules: `api/selectoption/approval/availablerules`,
      holdTypes: 'api/selectoption/approvals/holdTypes'
    },
    obtUserManagement: {
      companyContactsSearch: (companyId: number) => `api/selectoption/companyContactsSearch/${companyId}`
    },
    postmark: {
      suppressionReasons: 'api/selectoption/postmark/suppressionReasons'
    },
    userAdmin: {
      mainDbRoles: 'admin/user/filteredroles',
      obtRoles: 'api/admin/obtuser/filteredroles',
      usersByEmail: '/api/selectoption/usersByEmail'
    },
    address: {
      countries: '/api/selectoption/address/countries',
      provinces: (countryId: number | undefined) => `/api/selectoption/address/${countryId}/provinces`,
      cities: (provinceId: number | undefined): string => `/api/selectoption/address/${provinceId}/cities`,
      createCity: (provinceId: number | undefined): string => `/api/selectoption/address/${provinceId}/cities/create`
    },
    hotels: {
      cancellationPolicies: 'api/selectoption/hotels/cancellationpolicies',
      roomTypes: 'api/selectoption/hotels/roomtypes',
      hotelList: 'api/selectoption/hotels',
      byId: (hotelId: number) => `api/selectoption/hotels/${hotelId}`,
      hotelInCity: (cityId: number) => `api/selectoption/hotels/city/${cityId}`,
      hotelInCities: (cityIds: number[]) => `api/selectoption/hotels/cities?${queryStringArray('cityIds', cityIds)}`,
      chainCodes: `api/selectoption/hotels/chaincodes`,
      taxRates: (hotelId: number) => `api/selectoption/hotels/taxrates/${hotelId}`,
      amenties: (hotelId: number) => `api/selectoption/hotels/amenities/${hotelId}`,
      paymentMethods: `api/selectoption/hotels/paymentMethods`
    },
    activities: {
      cancellationPolicy: `${roots.selectOption}/activities/CancellationPolicy`,
      additionalChargeTypes: `${roots.selectOption}/activities/AdditionalChargeTypes`,
      itineraryHeaders: `${roots.selectOption}/activities/itinerary/headers`,
      activityStatus: (activityId: number | undefined) =>
        activityId
          ? `${roots.selectOption}/activities/${activityId}/status`
          : `${roots.selectOption}/activities/new/status`,
      activityAssign: (activityId: number | undefined, allowSelectingDisabled?: boolean) =>
        activityId
          ? `${roots.selectOption}/activities/${activityId}/AssignTo?allowSelectingDisabled=${allowSelectingDisabled ?? false}`
          : `${roots.selectOption}/activities/new/AssignTo?allowSelectingDisabled=${allowSelectingDisabled ?? false}`,
      cancelledAssign: (allowSelectingDisabled?: boolean) =>
        `${roots.selectOption}/activities/cancelledAssignTo?allowSelectingDisabled=${allowSelectingDisabled ?? false}`
    },
    companies: {
      list: (allowSelectingDisabled?: boolean, hasActiveCompanyProjects?: boolean) =>
        `api/SelectOption/Companies?allowSelectingDisabled=${allowSelectingDisabled ?? false}&hasActiveCompanyProjects=${hasActiveCompanyProjects ?? false}`,
      approvers: (companyId: number) => `/api/SelectOption/companies/approvers/${companyId}`,
      availableContactApprovers: (companyId: number, contactId: number | undefined) =>
        `api/SelectOption/companies/${companyId}/contacts/${contactId ?? 'new'}/availableApprovers`,
      branchOffices: (companyId: number) => `api/selectoption/companies/${companyId}/branchOffices`,
      thirdPartyBilling: 'api/SelectOption/ThirdPartyBillingCompanies',
      companyContactsSearch: (
        companyId: number | undefined | null,
        approvedBookerOnly: boolean,
        hotelBookingType: HotelBookingType | undefined,
        allowSelectingDisabled?: boolean,
        fuzzySearch?: boolean,
        companyContactTypes?: CompanyContactType[]
      ) =>
        `api/SelectOption/companies/contacts/${companyId}?hotelBookingType=${hotelBookingType == undefined ? HotelBookingType.Business : hotelBookingType}&approvedBookerOnly=${approvedBookerOnly}&allowSelectingDisabled=${allowSelectingDisabled ?? false}&fuzzySearch=${fuzzySearch ?? false}&${queryStringArray('companyContactTypes', companyContactTypes ?? [])}`,
      companyContactsList: (
        companyId: number | undefined | null,
        approvedBookerOnly: boolean,
        allowSelectingDisabled?: boolean,
        companyContactTypes?: CompanyContactType[]
      ) =>
        `api/SelectOption/companies/contacts/${companyId}/list/?approvedBookerOnly=${approvedBookerOnly}&allowSelectingDisabled=${allowSelectingDisabled ?? false}&${queryStringArray('companyContactTypes', companyContactTypes ?? [])}`,
      availablePayments: (
        companyId: number | undefined | null,
        travellerId: number | undefined | null,
        hotelBookingType?: HotelBookingType
      ) =>
        `api/SelectOption/companies/${companyId}/travellers/${travellerId}/availablePayments?hotelBookingType=${hotelBookingType ?? HotelBookingType.Business}`,
      companyRelatedCompanies: (companyId: number | undefined | null) =>
        `api/SelectOption/companies/${companyId}/relatedCompanies`,
      companyProjects: (companyId: number | undefined) => `api/SelectOption/companies/${companyId}/projects`
    },
    reportingRequirements: (companyId: number) => `api/SelectOption/ReportingRequirements/${companyId}`,
    formsOfPayment: 'api/SelectOption/FormsOfPayment',
    amenities: 'api/SelectOption/Amenities',
    faIcons: 'api/SelectOption/faIcons',
    industries: 'api/SelectOption/Industries',
    companyFormOfPayment: 'api/SelectOption/CompanyFormOfPayment',
    rateCodes: 'api/SelectOption/RateCodes',
    loyaltyAccount: 'api/SelectOption/LoyaltyAccount',
    leadSource: 'api/SelectOption/LeadSource',
    companyReportingRequirement: (companyId: number) => `api/SelectOption/CompanyReportingRequirement/${companyId}`,
    cities: () => `api/selectoption/cities`,
    cityById: (cityId: number) => `api/selectoption/cities/${cityId}`,
    companyProjectsCities: 'api/SelectOption/company-projects-cities',
    companyProjectGroupTypes: 'api/SelectOption/enum/CompanyProjectGroupType?includeZero=true',
    usersInRoles: (roles: string[], includeDisabled?: boolean) => {
      let toReturn = `/api/selectoption/UsersInRoles`;
      roles.forEach((role) => {
        if (toReturn.indexOf('?') >= 0) {
          toReturn += `&roles=${role}`;
        } else {
          toReturn += `?roles=${role}`;
        }
      });

      const includeDisabledFlag = includeDisabled ?? false;
      if (toReturn.indexOf('?') >= 0) {
        toReturn += `&includeDisabled=${includeDisabledFlag}`;
      } else {
        toReturn += `?includeDisabled=${includeDisabledFlag}`;
      }

      return toReturn;
    },
    enum: (typeName: string, includeZero: boolean = false) =>
      `/api/selectoption/Enum/${typeName}?includeZero=${includeZero}`,
    serviceFeeTemplates: 'api/SelectOption/serviceFeeTemplates',
    convenienceFeeTemplates: 'api/SelectOption/convenienceFeeTemplates',
    hotelServiceFeeTemplates: 'api/SelectOption/hotelServiceFeeTemplates',
    mccProfiles: 'api/SelectOption/mccProfiles',
    BookingAllowedCountries: 'api/SelectOption/BookingAllowedCountries',
    companyProjectHotels: (projectId: number | undefined) => `api/SelectOption/projects/${projectId}/hotels`
  },

  company: {
    projects: (id?: number, cityName: string[] = [], cityIds: number[] = [], start?: string, end?: string) =>
      `/api/Company/${id}/projects${
        cityName || start || end
          ? `?${[
              cityIds.length ? cityIds.map((city) => `cityIds=${encodeURIComponent(city)}`).join('&') : '',
              cityName.length ? cityName.map((city) => `cityName=${encodeURIComponent(city)}`).join('&') : '',
              start && `start=${encodeURIComponent(start)}`,
              end && `end=${encodeURIComponent(end)}`
            ]
              .filter(Boolean)
              .join('&')}`
          : ''
      }`
  },

  activityReport: (page: number, rowsPerPage: number) => `/api/activityreport?page=${page}&pageSize=${rowsPerPage}`,

  manualWex: {
    getCards: (hotelBookingType?: HotelBookingType) =>
      `/api/ManualWex/GetWexCards?hotelBookingType=${hotelBookingType ?? HotelBookingType.Business}`,
    transactions: (manualWexCardId: number) => `/api/ManualWex/${manualWexCardId}/Transactions`,
    create: '/api/ManualWex/GenerateWexCard',
    update: '/api/ManualWex/UpdateWexCard'
  },

  ...ComposeEndpoints,
  ...ProjectEndpoints,
  preReconciliation: {
    invoice: {
      getPreview: (activityId?: number) => `/api/prereconciliation/${activityId}/invoice`
    },
    invoiceGroup: {
      filteredListPost: (page: number, rowsPerPage: number, orderBy?: string, isDesc?: boolean) => {
        let toReturn = `/api/InvoiceGroup/filteredListPost?page=${page}&pageSize=${rowsPerPage}`;
        if (orderBy !== undefined) {
          toReturn += `&orderBy=${orderBy}`;
        }
        if (isDesc !== undefined) {
          toReturn += `&isDesc=${isDesc}`;
        }
        return toReturn;
      },
      updateInvoiceGroupDate: '/api/InvoiceGroup/UpdateInvoiceGroupDate',
      report: '/api/InvoiceGroup/Report',
      getInvoiceInformation: (companyId: number) => `/api/InvoiceGroup/InvoiceInformation/${companyId}`
    }
  },
  serviceFees: {
    calculate: (
      checkInDate: string,
      checkOutDate: string,
      serviceTemplateId?: number | null,
      isHotelTemplate?: boolean
    ) =>
      `/api/servicefee/calculate/${serviceTemplateId}?checkInDate=${checkInDate}&checkOutDate=${checkOutDate}${isHotelTemplate !== undefined ? `&isHotelTemplate=${isHotelTemplate}` : ''}`,
    getByCompanyId: (companyId: number) => `/api/servicefee/company/${companyId}`,
    getByHotelId: (hotelId: number) => `/api/servicefee/hotel/${hotelId}`
  },

  mirAdmin: {
    mirFileReport: '/api/MirAdmin/MirFileReport',
    mirAgentErrorReport: '/api/MirAgentErrorReport',
    mirActivityReport: '/api/MirAdmin/ActivityMissingMirReport',
    put: '/api/MirAdmin',
    delete: (id: number) => `/api/MirAdmin/${id}`,
    import: '/api/MirAdmin/RunImport'
  },
  crmAdmin: {
    announcements: {
      basicCrud: '/crmadmin/announcements',
      getCurrent: '/crmadmin/announcements/getCurrent'
    },
    obtAnnouncements: {
      basicCrud: '/crmadmin/obtannouncements'
    },
    loyaltyAccountManagement: '/crmadmin/loyaltynumbertypes',
    HotelBrands: {
      get: (brandId: number) => `/crmadmin/hotelBrandManagement/${brandId}`,
      filteredListPost: (
        page: number,
        pageSize: number,
        orderBy: string | undefined,
        isDesc: boolean | undefined,
        globalSearchString?: string
      ) => {
        let toReturn = `/crmadmin/hotelBrandManagement/filter?page=${page}&pageSize=${pageSize}`;
        if (orderBy !== undefined) {
          toReturn += `&orderBy=${orderBy}`;
        }
        if (isDesc !== undefined) {
          toReturn += `&isDesc=${isDesc}`;
        }
        if (globalSearchString !== undefined) {
          toReturn += `&globalSearch=${globalSearchString}`;
        }
        return toReturn;
      },
      create: `/crmadmin/hotelBrandManagement/create`,
      update: (brandId: number) => `/crmadmin/hotelBrandManagement/${brandId}/update`,
      toggleActive: (brandId?: number) => `/crmadmin/hotelBrandManagement/${brandId}/toggleActive`
    },
    roomtypes: '/crmadmin/roomtypes',
    cancellationpolicies: '/crmadmin/cancellationpolicy',
    additionalcharges: '/crmadmin/additionalcharges',
    creditcardtypes: {
      basicCrud: '/crmadmin/creditcardtypes',
      search: (page: number, pageSize: number, orderBy?: string, isDesc?: boolean, globalSearch?: string) => {
        let toReturn = `/crmadmin/creditcardtypes/search?page=${page}&pageSize=${pageSize}`;
        if (orderBy !== undefined) {
          toReturn += `&orderBy=${orderBy}`;
        }
        if (isDesc !== undefined) {
          toReturn += `&isDesc=${isDesc}`;
        }
        if (globalSearch !== undefined) {
          toReturn += `&globalSearch=${globalSearch}`;
        }
        return toReturn;
      }
    },
    hotelRateKeywords: '/crmadmin/hotelRateKeywords',
    foliomanagement: '/crmadmin/foliomanagement',
    industryManagement: '/crmadmin/industries',
    issueSubCategories: '/crmadmin/issueSubCategories',
    reportingRequirementManagement: '/crmadmin/reportingrequirements',
    itineraryHeaders: '/crmadmin/itineraryheaders',
    rateCode: {
      basicCrud: 'crmadmin/rateCodes',
      search: (page: number, pageSize: number, orderBy?: string, isDesc?: boolean) => {
        let toReturn = `crmadmin/rateCodes/search?page=${page}&pageSize=${pageSize}`;
        if (orderBy !== undefined) {
          toReturn += `&orderBy=${orderBy}`;
        }
        if (isDesc !== undefined) {
          toReturn += `&isDesc=${isDesc}`;
        }
        return toReturn;
      }
    },
    serviceFee: {
      basicCrud: 'crmadmin/serviceFees'
    },
    convenienceFee: {
      basicCrud: 'crmadmin/convenienceFeeTemplates'
    },
    hotelServiceFee: {
      basicCrud: 'crmadmin/hotelServiceFees'
    },
    taxRate: {
      basicCrud: 'crmadmin/taxrates',
      associatedHotels: (taxRateId?: number) => `crmadmin/taxrates/${taxRateId}/associatedHotels`,
      delete: (taxRateId?: number) => `crmadmin/taxrates/${taxRateId}/delete`
    },
    taxConfiguration: {
      get: '/crmadmin/taxConfiguration',
      updateGst: '/crmadmin/taxConfiguration/gst',
      updateProvince: (provinceId: number) => `/crmadmin/taxConfiguration/province/${provinceId}`,
      createOtherTax: (provinceId: number) => `/crmadmin/taxConfiguration/province/${provinceId}/otherTax`,
      updateOtherTax: (id: number) => `/crmadmin/taxConfiguration/otherTax/${id}`,
      deleteOtherTax: (id: number) => `/crmadmin/taxConfiguration/otherTax/${id}`,
      createCityTax: '/crmadmin/taxConfiguration/cityTax',
      updateCityTax: (id: number) => `/crmadmin/taxConfiguration/cityTax/${id}`,
      deleteCityTax: (id: number) => `/crmadmin/taxConfiguration/cityTax/${id}`
    },
    amenities: {
      basicCrud: 'crmadmin/amenities',
      associatedEntries: (amenityId?: number) => `crmadmin/amenities/${amenityId}/associatedEntries`,
      get: (amenityId?: number) => `crmadmin/amenities/${amenityId}/get`,
      delete: (amenityId?: number) => `crmadmin/amenities/${amenityId}/delete`
    }
  },
  hubs: {
    activityNotificationHub: API_URL + '/hubs/ActivityOpenNotificationHub',
    activityReplyNotificationHub: API_URL + '/hubs/ActivityReplyNotificationHub',
    workFlowHubV2: API_URL + '/hubs/WorkflowHub2',
    agentDrawer: API_URL + '/hubs/agentDrawer',
    agentInbox: API_URL + '/hubs/inbox',
    folioRequests: API_URL + '/hubs/folioRequests',
    folioRequestsHotelOpenNotificationHub: API_URL + '/hubs/FolioRequestsHotelOpenNotificationHub',
    projectDashboard: API_URL + '/hubs/projectDashboard',
    projectEditLock: API_URL + '/hubs/projectEditLock'
  },
  ...ReportingEndpoints,
  systemSettings: {
    basicCrud: '/api/admin/systemsettings',
    get: (systemSettingId?: number) => `/api/admin/systemSettings/${systemSettingId}/get`,
    getByKey: (key: string) => `/api/systemSettings/GetSystemSetting?key=${key}`
  },
  quickActions: {
    basicCrud: '/api/admin/quickactions',
    get: (quickActionId?: number) => `/api/admin/quickactions/${quickActionId}/get`
  },
  mccProfile: {
    basicCrud: '/api/admin/merchantcategorycode',
    get: (mccProfileId?: number) => `/api/admin/merchantcategorycode/${mccProfileId}/get`
  },
  obtUserManagement: {
    createNewFrontierUserForOBT: `api/admin/obtuser/CreateNewFrontierUserForOBT`,
    updateFrontierUserForOBT: `api/admin/obtuser/UpdateFrontierUserForOBT`,
    updateOrCreateUser: `api/admin/obtuser`,
    changePassword: `api/admin/obtuser/ChangePassword`,
    requestPasswordReset: (email: string) => `api/admin/obtuser/SendObtPasswordResetEmail?email=${email}`,
    getSingleObtUserModel: (userId?: string) => `api/admin/obtuser/GetSingleObtUserModel?userId=${userId}`,
    createStandardObtAccess: `api/admin/obtuser/createStandardObtAccess`,
    getObtFrontierUserContactModelsByContactId: (contactId: number) =>
      `api/admin/obtuser/GetObtFrontierUserContactModelsByContactId?contactId=${contactId}`,
    updateObtAccessForContactId: (contactId: number) =>
      `api/admin/obtuser/UpdateObtAccessForContactId?contactId=${contactId}`,
    exportToExcel: `api/admin/obtuser/exportToExcel`
  },
  files: {
    get: (fileHandle: string) => `/api/File?fileHandle=${fileHandle}`
  },
  thirdPartyViewer: {
    getCardDetailsFromGuid: (guid: string, isGroup: boolean) =>
      `/api/ThirdPartyPaymentViewer/GetThirdPartyBillingDetailsFromGuid?guid=${guid}&isGroup=${isGroup}`,
    getCardDetailsFromCompany: (companyId: number, cardId: number) =>
      `/api/CompanyAccounting/${companyId}/ViewDecryptedThirdPartyCardDetails/${cardId}`
  },
  emailSignatureAdmin: {
    getEmailSignatures: () => `/api/admin/emailsignature`,
    updateEmailSignature: () => `/api/admin/emailsignature`,
    deleteEmailSignature: (id: number) => `/api/admin/emailsignature/${id}`
  },
  quickDocuments: {
    getQuickDocuments: (page: number, pageSize: number, orderBy?: string, isDesc?: boolean) => {
      let toReturn = `/api/admin/DocumentManagement?page=${page}&pageSize=${pageSize}`;
      if (orderBy !== undefined) {
        toReturn += `&orderBy=${orderBy}`;
      }
      if (isDesc !== undefined) {
        toReturn += `&isDesc=${isDesc}`;
      }
      return toReturn;
    },
    createQuickDocument: () => `/api/admin/DocumentManagement`,
    updateQuickDocument: (id: number) => `/api/admin/DocumentManagement/${id}`,
    deleteQuickDocument: (id: number) => `/api/admin/DocumentManagement/${id}`
  },
  activityInformation: {
    status: (activityId: number | undefined) =>
      activityId
        ? `${roots.selectOption}/activities/${activityId}/status`
        : `${roots.selectOption}/activities/new/status`,
    assign: (activityId: number | undefined, allowSelectingDisabled?: boolean) =>
      activityId
        ? `${roots.selectOption}/activities/${activityId}/AssignTo?allowSelectingDisabled=${allowSelectingDisabled ?? false}`
        : `${roots.selectOption}/activities/new/AssignTo?allowSelectingDisabled=${allowSelectingDisabled ?? false}`
  },
  folioCollection: FolioEndpoints.folioCollection
};
